// @ts-nocheck
import { useRef, useEffect, useState, useCallback } from "react";
import { CredentialResponse } from "./google";

interface GoogleLoginProps {
    clientId: string;
    handleGoogleSignIn: (res: CredentialResponse) => void;
}

export function GoogleLogin(props: Readonly<GoogleLoginProps>) {
    const [gsiScriptLoaded, setGsiScriptLoaded] = useState(false);
    const divRef = useRef<HTMLDivElement>(null);
    const [googleIsLoading, setGoogleIsLoading] = useState(true);

    function useInterval(callback: () => void, delay: number | null) {
        const savedCallback = useRef(callback);

        // Remember the latest callback if it changes.
        useEffect(() => {
            savedCallback.current = callback;
        }, [callback]);

        // Set up the interval.
        useEffect(() => {
            // Don't schedule if no delay is specified.
            // Note: 0 is a valid value for delay.
            if (!delay && delay !== 0) {
                return;
            }

            const id = setInterval(() => savedCallback.current(), delay);

            return () => clearInterval(id);
        }, [delay]);
    }

    useInterval(
        () => {
            if (typeof window !== "undefined" && window.google) {
                setGoogleIsLoading(false);
            }
        },
        googleIsLoading ? 100 : null
    );

    const initializeGsi = useCallback(() => {
        // Typescript will complain about window.google
        // Add types to your `react-app-env.d.ts` or //@ts-ignore it.
        if (!window.google || !divRef.current || gsiScriptLoaded) return;

        setGsiScriptLoaded(true);
        window.google.accounts.id.initialize({
            client_id: props.clientId,
            callback: props.handleGoogleSignIn,
        });

        window.google.accounts.id.renderButton(
            divRef.current,
            { type: "standard", theme: "outline", size: "large" } // customization attributes
        );

        window.google.accounts.id.prompt(); // also display the One Tap dialog
    }, [gsiScriptLoaded, props.clientId, props.handleGoogleSignIn]);

    useEffect(() => {
        if (gsiScriptLoaded) return;

        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.onload = initializeGsi;
        script.async = true;
        script.id = "google-client-script";
        document.querySelector("body")?.appendChild(script);

        return () => {
            // Cleanup function that runs when component unmounts
            window.google?.accounts.id.cancel();
            document.getElementById("google-client-script")?.remove();
        };
    }, [gsiScriptLoaded, initializeGsi]);

    return <div ref={divRef}></div>;
}
